import React from "react";

import { CheckIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OWNER_TERMS_VERSION } from "@/constants/ownerOnboarding";
import { useAuth } from "@/context/auth";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger";

import { useOwnerOnboarding } from "../-owner-onboarding-context";
import { useWorkerOnboarding } from "../-worker-onboarding-context";

interface Props {
  onContinue?: (handler: () => Promise<boolean>) => void;
}

const TC_VERSION = "1.0-beta";
const PP_VERSION = "1.0";
const EFFECTIVE_DATE = "May 2026";

/* ─────────────────────────────────────────────────────────────────────────── */
/* Worker variant — local state, no server-side acceptance record              */
/* ─────────────────────────────────────────────────────────────────────────── */

export function WorkerLegalAgreementsStep({ onContinue }: Props) {
  const { t } = useTranslation("onboarding");
  const auth = useAuth();
  const { saveDraft } = useWorkerOnboarding();
  const isEditMode = Boolean(auth.user?.onboardingCompleted);
  const [tcAccepted, setTcAccepted] = React.useState(false);
  const [ppAccepted, setPpAccepted] = React.useState(false);

  React.useEffect(() => {
    if (!onContinue) return;
    onContinue(async () => {
      // In edit mode the user already accepted at onboarding; the step is
      // read-only, so just save any unrelated draft changes and pass through.
      if (!isEditMode) {
        if (!tcAccepted) {
          notify.error(t("legalAgreements.mustAcceptTerms"));
          return false;
        }
        if (!ppAccepted) {
          notify.error(t("legalAgreements.mustAcceptPrivacy"));
          return false;
        }
      }
      try {
        // Mirror the legacy flow's saveDraft+complete in a single click so
        // /complete validates against the very latest draft state. Without
        // this, any re-hydration between the Review step's save and Complete
        // can leave the BE seeing a stale snapshot.
        await saveDraft();
        return true;
      } catch (e) {
        Logger.error("worker legal agreements save failed", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(
          e instanceof Error
            ? e.message
            : t("workerReview.saveFailed"),
        );
        return false;
      }
    });
  }, [onContinue, tcAccepted, ppAccepted, isEditMode, saveDraft, t]);

  return isEditMode ? (
    <LegalAgreementsBody mode="review" />
  ) : (
    <LegalAgreementsBody
      mode="consent"
      tcAccepted={tcAccepted}
      ppAccepted={ppAccepted}
      onTcChange={setTcAccepted}
      onPpChange={setPpAccepted}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Owner variant — hydrates from owner onboarding context and persists         */
/* acceptance server-side via acceptTerms() on continue.                       */
/* ─────────────────────────────────────────────────────────────────────────── */

export function OwnerLegalAgreementsStep({ onContinue }: Props) {
  const { t } = useTranslation("onboarding");
  const auth = useAuth();
  const { data, acceptTerms, saveDraft } = useOwnerOnboarding();
  const isEditMode = Boolean(auth.user?.onboardingCompleted);

  // Returning users already accepted server-side; pre-check both rows so they
  // don't have to re-tick on edit. The BE tracks a single `termsAccepted` flag
  // covering the combined T&C + Privacy acknowledgement.
  const accepted =
    data.termsAccepted && data.termsVersion === OWNER_TERMS_VERSION;
  const [tcAccepted, setTcAccepted] = React.useState(accepted);
  const [ppAccepted, setPpAccepted] = React.useState(accepted);

  // Sync once the draft hydrates (data may load async after first render).
  React.useEffect(() => {
    if (accepted) {
      setTcAccepted(true);
      setPpAccepted(true);
    }
  }, [accepted]);

  React.useEffect(() => {
    if (!onContinue) return;
    onContinue(async () => {
      // In edit mode the user already accepted at first-time onboarding;
      // the step is read-only, so skip the checkbox gate and the
      // acceptTerms re-call.
      if (!isEditMode) {
        if (!tcAccepted) {
          notify.error(t("legalAgreements.mustAcceptTerms"));
          return false;
        }
        if (!ppAccepted) {
          notify.error(t("legalAgreements.mustAcceptPrivacy"));
          return false;
        }
      }
      try {
        // Same defensive save as the worker variant: ensure /complete sees
        // the latest draft state, not a snapshot from an earlier step.
        await saveDraft();
        if (
          !isEditMode &&
          (!data.termsAccepted || data.termsVersion !== OWNER_TERMS_VERSION)
        ) {
          await acceptTerms(OWNER_TERMS_VERSION);
        }
        return true;
      } catch (e) {
        Logger.error("owner legal agreements acceptance failed", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(
          e instanceof Error ? e.message : t("legalAgreementsChrome.recordFailed"),
        );
        return false;
      }
    });
  }, [
    onContinue,
    tcAccepted,
    ppAccepted,
    isEditMode,
    data.termsAccepted,
    data.termsVersion,
    acceptTerms,
    saveDraft,
    t,
  ]);

  return isEditMode ? (
    <LegalAgreementsBody mode="review" />
  ) : (
    <LegalAgreementsBody
      mode="consent"
      tcAccepted={tcAccepted}
      ppAccepted={ppAccepted}
      onTcChange={setTcAccepted}
      onPpChange={setPpAccepted}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Shared body — header, tabs, content panels, consent checkboxes              */
/* ─────────────────────────────────────────────────────────────────────────── */

type LegalAgreementsBodyProps =
  | {
      mode: "consent";
      tcAccepted: boolean;
      ppAccepted: boolean;
      onTcChange: (v: boolean) => void;
      onPpChange: (v: boolean) => void;
    }
  | { mode: "review" };

export function LegalAgreementsBody(props: LegalAgreementsBodyProps) {
  const { t } = useTranslation("onboarding");
  const [activeTab, setActiveTab] = React.useState<"tc" | "pp">("tc");
  const isReview = props.mode === "review";

  return (
    <section className="w-full flex flex-col gap-5">
      <header className="flex flex-col gap-2 pb-4 border-b border-garnet/15">
        <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ember">
          {t("legalAgreementsChrome.companyName")}
        </span>
        <h2 className="heading-1">{t("legalAgreementsChrome.heading")}</h2>
        <p className="body-small text-slate">
          {isReview
            ? t("legalAgreementsChrome.reviewMode")
            : t("legalAgreementsChrome.consentMode")}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate mt-1">
          <span>{t("legalAgreementsChrome.effectiveDate", { date: EFFECTIVE_DATE })}</span>
          <span>·</span>
          <span>{t("legalAgreementsChrome.jurisdiction")}</span>
          <span>·</span>
          <span>
            {t("legalAgreementsChrome.versionLine", {
              tcVersion: TC_VERSION,
              ppVersion: PP_VERSION,
            })}
          </span>
        </div>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as "tc" | "pp")}
        className="w-full"
      >
        <TabsList className="w-full">
          <TabsTrigger value="tc">{t("legalAgreementsChrome.tabTerms")}</TabsTrigger>
          <TabsTrigger value="pp">{t("legalAgreementsChrome.tabPrivacy")}</TabsTrigger>
        </TabsList>

        <TabsContent value="tc">
          <ScrollPanel>
            <TermsContent />
          </ScrollPanel>
        </TabsContent>

        <TabsContent value="pp">
          <ScrollPanel>
            <PrivacyContent />
          </ScrollPanel>
        </TabsContent>
      </Tabs>

      {isReview ? (
        <AcceptedNotice />
      ) : (
        <ConsentSection
          tcAccepted={props.tcAccepted}
          ppAccepted={props.ppAccepted}
          onTcChange={props.onTcChange}
          onPpChange={props.onPpChange}
          setActiveTab={setActiveTab}
        />
      )}
    </section>
  );
}

function ConsentSection({
  tcAccepted,
  ppAccepted,
  onTcChange,
  onPpChange,
  setActiveTab,
}: {
  tcAccepted: boolean;
  ppAccepted: boolean;
  onTcChange: (v: boolean) => void;
  onPpChange: (v: boolean) => void;
  setActiveTab: (tab: "tc" | "pp") => void;
}) {
  const { t } = useTranslation("onboarding");
  return (
    <div className="flex flex-col gap-3 mt-4">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-ember">
          {t("legalAgreementsChrome.requiredToContinue")}
        </span>
        <span className="flex-1 h-px bg-garnet/15" />
        <span className="text-[11px] font-semibold text-slate">
          {t("legalAgreementsChrome.acceptedCount", {
            accepted: (tcAccepted ? 1 : 0) + (ppAccepted ? 1 : 0),
          })}
        </span>
      </div>
      <ConsentRow
        id="legal-tc-accept"
        checked={tcAccepted}
        onChange={(v) => {
          onTcChange(v);
          if (v && !ppAccepted) setActiveTab("pp");
        }}
      >
        {t("legalAgreementsChrome.agreeTermsBefore")}
        <button
          type="button"
          className="underline text-ember font-semibold"
          onClick={(e) => {
            e.preventDefault();
            setActiveTab("tc");
          }}
        >
          {t("legalAgreementsChrome.agreeTermsLink")}
        </button>
        {t("legalAgreementsChrome.endPeriod")}
      </ConsentRow>

      <ConsentRow
        id="legal-pp-accept"
        checked={ppAccepted}
        onChange={onPpChange}
      >
        {t("legalAgreementsChrome.agreePrivacyBefore")}
        <button
          type="button"
          className="underline text-ember font-semibold"
          onClick={(e) => {
            e.preventDefault();
            setActiveTab("pp");
          }}
        >
          {t("legalAgreementsChrome.agreePrivacyLink")}
        </button>
        {t("legalAgreementsChrome.endPeriod")}
      </ConsentRow>
    </div>
  );
}

function AcceptedNotice() {
  const { t } = useTranslation("onboarding");
  return (
    <div className="flex flex-col gap-3 mt-4">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-ember">
          {t("legalAgreementsChrome.status")}
        </span>
        <span className="flex-1 h-px bg-garnet/15" />
      </div>
      <div
        className="flex flex-row items-start gap-4 p-4 rounded-lg ring-2 ring-garnet/30 bg-garnet/[0.04] shadow-sm"
        role="status"
      >
        <div className="size-7 shrink-0 rounded-full bg-garnet text-white flex items-center justify-center mt-0.5">
          <CheckIcon className="size-4 stroke-[3]" aria-hidden />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <p className="body-small font-semibold text-garnet">
            {t("legalAgreementsChrome.alreadyAccepted")}
          </p>
          <p className="text-[13px] leading-snug text-slate">
            {t("legalAgreementsChrome.alreadyAcceptedBody")}
          </p>
        </div>
      </div>
    </div>
  );
}

function ScrollPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-h-[55vh] overflow-y-auto rounded-md ring-1 ring-garnet/30 bg-white p-5 mt-3 prose-legal">
      {children}
    </div>
  );
}

function ConsentRow({
  id,
  checked,
  onChange,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label
      htmlFor={id}
      className={
        "flex flex-row gap-4 items-center p-4 rounded-lg ring-2 cursor-pointer transition-all shadow-sm " +
        (checked
          ? "ring-garnet bg-garnet/[0.04]"
          : "ring-garnet/30 bg-white hover:ring-garnet hover:bg-garnet/[0.02]")
      }
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(Boolean(v))}
        className="size-7 shrink-0 rounded-md border-2 border-garnet bg-white shadow-inner data-[state=checked]:bg-garnet data-[state=checked]:text-white data-[state=checked]:border-garnet [&_svg]:size-5 [&_svg]:stroke-[3]"
      />
      <span className="body-small flex-1 leading-snug">{children}</span>
    </label>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Shared legal-doc primitives                                                 */
/* ─────────────────────────────────────────────────────────────────────────── */

function DocSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mb-7 scroll-mt-4">
      <h3 className="text-[15px] font-bold text-garnet pb-1.5 mb-2.5 border-b border-garnet/15 tracking-tight">
        {title}
      </h3>
      <div className="flex flex-col gap-2.5 text-[13.5px] leading-[1.65] text-cocoa">
        {children}
      </div>
    </section>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-[13.5px] font-bold text-ember mt-3 mb-1">{children}</h4>
  );
}

function BulletList({ children }: { children: React.ReactNode }) {
  return (
    <ul className="flex flex-col gap-1.5 my-1 pl-5 list-disc marker:text-ember">
      {children}
    </ul>
  );
}

function Callout({
  tone = "info",
  children,
}: {
  tone?: "info" | "warn" | "accent";
  children: React.ReactNode;
}) {
  const base = "rounded-md px-4 py-3 my-3 text-[13px] leading-[1.6] border-l-[3px]";
  const palette: Record<typeof tone, string> = {
    info: "bg-garnet/5 border-garnet text-cocoa",
    accent: "bg-ember/5 border-ember text-cocoa",
    warn: "bg-amber-50 border-amber-400 text-cocoa",
  };
  return <div className={`${base} ${palette[tone]}`}>{children}</div>;
}

function MetaHeader({
  eyebrow,
  title,
  version,
}: {
  eyebrow: string;
  title: string;
  version: string;
}) {
  return (
    <header className="pb-4 mb-5 border-b border-garnet/20">
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ember mb-1">
        {eyebrow}
      </div>
      <h2 className="font-serif text-[26px] leading-tight text-garnet">
        {title}
      </h2>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-slate mt-2">
        <span>Effective Date: {EFFECTIVE_DATE}</span>
        <span>·</span>
        <span>
          Jurisdiction: California · LA County · Washington · King County ·
          Seattle
        </span>
        <span>·</span>
        <span>Version {version}</span>
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* TERMS & CONDITIONS                                                          */
/* Text taken verbatim from newTandC.html                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

export function TermsContent() {
  return (
    <article>
      <MetaHeader
        eyebrow="Public Market Media LLC"
        title="Terms & Conditions"
        version={`${TC_VERSION}`}
      />

      <Callout tone="warn">
        <strong>Please read these Terms carefully.</strong> By creating an
        account or using the Public Market Platform, you agree to be legally
        bound by these Terms. If you do not agree, do not use the Platform.
      </Callout>

      <DocSection id="tc-1" title="1. About Public Market & Acceptance of Terms">
        <p>
          Public Market ("Platform," "we," "us," or "our") is an online
          marketplace operated by Public Market Media LLC, a California limited
          liability company, that connects food service workers ("Workers")
          with restaurants, food establishments, and hospitality venues
          ("Employers") for temporary, on-demand, or project-based engagements.
          The Platform currently operates in the State of California (including
          Los Angeles County and its incorporated cities) and the State of
          Washington (including King County and the City of Seattle).
        </p>
        <p>
          By registering, you represent that you are at least 18 years of age
          and have full legal authority to enter into a binding agreement.
          These Terms apply to all users including Workers, Employers, and
          visitors. We reserve the right to modify these Terms at any time;
          continued use constitutes acceptance of any changes.
        </p>
      </DocSection>

      <DocSection id="tc-2" title="2. Independent Contractor Classification">
        <Callout tone="warn">
          <strong>Critical — please read carefully.</strong>
        </Callout>

        <SubHeading>2.1 Workers Are Independent Contractors</SubHeading>
        <p>
          Workers using Public Market are independent contractors, NOT
          employees of Public Market Media LLC or any Employer. This
          classification is consistent with California AB5, the ABC Test under
          California Labor Code Section 2775 et seq., Washington State's
          independent contractor standards under RCW 49.46 and WAC 296-128, and
          applicable federal law. Workers expressly acknowledge:
        </p>
        <BulletList>
          <li>
            They are free from control and direction of Public Market Media LLC
            in performing work
          </li>
          <li>
            Work performed is outside the usual course of Public Market Media
            LLC's business (marketplace facilitation, not food service
            operations)
          </li>
          <li>
            Workers are customarily engaged in an independently established
            trade, occupation, or business
          </li>
          <li>
            Workers may work for multiple Employers simultaneously and are free
            to accept or decline any engagement
          </li>
          <li>
            Workers are responsible for all tools, certifications, and licenses
            required for their work unless otherwise agreed in writing with the
            Employer
          </li>
          <li>
            Workers are solely responsible for all applicable federal, state,
            and local taxes on income earned
          </li>
          <li>
            Workers are not entitled to workers' compensation, unemployment
            insurance, health benefits, paid time off, overtime, or any other
            employee benefits from Public Market Media LLC
          </li>
        </BulletList>

        <SubHeading>2.2 Employer Acknowledgment</SubHeading>
        <p>
          Employers acknowledge they are engaging Workers as independent
          contractors for specific engagements and are solely responsible for
          correct worker classification under California and federal law, wage
          and hour compliance if a worker is later classified as an employee,
          and maintaining their own workers' compensation policies.
        </p>
        <Callout tone="info">
          <strong>LA County Note:</strong> California's strict ABC Test under
          AB5 requires careful compliance. Employers in the City of Los
          Angeles, Santa Monica, Pasadena, Long Beach, and other LA County
          municipalities may have additional local wage ordinances applicable
          to their workers.
          <br />
          <br />
          <strong>King County / Seattle Note:</strong> Washington State applies
          its own economic realities test for worker classification under the
          Washington Minimum Wage Act (RCW 49.46.010). Employers operating in
          Seattle and King County must also comply with the Seattle Minimum
          Wage Ordinance (SMC 14.19), Seattle's Gig Worker Minimum Compensation
          Ordinance (SMC 14.34), and any applicable King County labor
          regulations. Compliance is the sole responsibility of Employers.
        </Callout>

        <SubHeading>2.3 No Employment Relationship with Platform</SubHeading>
        <p>
          Public Market Media LLC does not supervise, direct, control, or
          manage how Workers perform services. The Platform is solely a
          technology-enabled marketplace. Nothing in these Terms creates an
          employment, staffing agency, or labor contracting relationship
          between Public Market Media LLC and any user.
        </p>
      </DocSection>

      <DocSection
        id="tc-3"
        title="3. Eligibility, Registration & Account Security"
      >
        <SubHeading>3.1 Eligibility</SubHeading>
        <BulletList>
          <li>Be at least 18 years of age</li>
          <li>Have the legal right to work in the United States</li>
          <li>
            Hold any applicable food handler's permits, certifications (e.g.,
            ServSafe, California Food Handler Card, or Washington State Food
            Worker Card issued under WAC 246-215), or other licenses required
            by applicable state, county, or municipal codes in the jurisdiction
            where you are working
          </li>
          <li>
            Not be barred from using the Platform under any prior suspension or
            termination
          </li>
        </BulletList>
        <SubHeading>3.2 Account Registration & Security</SubHeading>
        <p>
          You agree to provide accurate, current, and complete information
          during registration. You are responsible for maintaining the
          confidentiality of your login credentials. Notify us immediately at
          support@publicmarket.com of any unauthorized use. Public Market Media
          LLC is not liable for losses resulting from unauthorized account
          access caused by your failure to safeguard credentials.
        </p>
      </DocSection>

      <DocSection id="tc-4" title="4. Platform Use, Engagements & Conduct">
        <SubHeading>4.1 Engagement Process</SubHeading>
        <p>
          Employers post available shifts or roles. Workers may apply for or
          accept engagements. An engagement forms when an Employer confirms a
          Worker's acceptance. Each engagement is a separate agreement between
          Employer and Worker; Public Market Media LLC is not a party.
        </p>

        <SubHeading>4.2 Worker Conduct</SubHeading>
        <BulletList>
          <li>Arrive on time and prepared for all confirmed engagements</li>
          <li>
            Maintain professional conduct, hygiene, and food safety standards
            required by applicable state food codes — including the California
            Retail Food Code (Health &amp; Safety Code § 113700 et seq.) and LA
            County DPH regulations in California, and the Washington State
            Retail Food Code (WAC 246-215) and Seattle/King County Public
            Health regulations in Washington
          </li>
          <li>
            Immediately disclose to Employers any health conditions that may
            affect food safety
          </li>
          <li>
            Not misrepresent qualifications, certifications, or experience on
            the Platform
          </li>
          <li>
            Comply with all Employer workplace policies communicated before or
            during an engagement
          </li>
        </BulletList>

        <SubHeading>4.3 Employer Conduct</SubHeading>
        <BulletList>
          <li>
            Provide accurate and complete descriptions of shift requirements,
            working conditions, and compensation
          </li>
          <li>
            Comply with all applicable workplace safety regulations — including
            Cal/OSHA (8 CCR) in California and Washington Industrial Safety and
            Health Act (WISHA) standards under WAC 296-800 in Washington — as
            well as applicable county and local health codes
          </li>
          <li>
            Not discriminate based on any characteristic protected under
            California's Fair Employment and Housing Act (FEHA), the Unruh
            Civil Rights Act, Washington's Law Against Discrimination (RCW
            49.60), the Seattle Fair Employment Practices Ordinance (SMC
            14.04), or applicable local anti-discrimination ordinances
          </li>
          <li>
            Pay Workers the compensation agreed upon within the timeframes set
            forth in these Terms
          </li>
        </BulletList>

        <SubHeading>4.4 Prohibited Conduct</SubHeading>
        <BulletList>
          <li>Providing false identity, credentials, or certifications</li>
          <li>
            Soliciting other users to transact outside the Platform to
            circumvent fees
          </li>
          <li>
            Harassment, discrimination, or abusive behavior toward any user
          </li>
          <li>Posting fraudulent, misleading, or illegal job listings</li>
          <li>
            Any conduct that violates applicable local, state, or federal law
          </li>
        </BulletList>
      </DocSection>

      <DocSection id="tc-5" title="5. Compensation & Payment Terms">
        <Callout tone="accent">
          <strong>
            Public Market does not process, facilitate, hold, transmit, or
            intermediate payments.
          </strong>{" "}
          All compensation is agreed upon and transacted solely and directly
          between Employers and Workers. Public Market Media LLC is not a
          payment processor, money transmitter, or financial intermediary and
          bears no responsibility for compensation between parties.
        </Callout>

        <SubHeading>5.1 Employer Payment Obligations</SubHeading>
        <p>Employers are solely responsible for:</p>
        <BulletList>
          <li>
            Agreeing to and communicating a clear compensation rate to Workers
            prior to engagement confirmation
          </li>
          <li>
            Paying Workers directly via their own agreed-upon method (cash,
            check, ACH, third-party app) on the schedule mutually agreed with
            the Worker
          </li>
          <li>
            Ensuring compensation meets or exceeds all applicable federal,
            state, and local minimum wage requirements — including California
            state minimum wage and LA County/city ordinances, and Washington
            State minimum wage (RCW 49.46.020) and the Seattle Minimum Wage
            Ordinance (SMC 14.19) and any applicable King County minimum wage
            requirements
          </li>
          <li>
            Complying with applicable state wage payment laws — California
            Labor Code Section 200 et seq. and Washington's Wage Payment Act
            (RCW 49.48) — regarding timely payment of wages
          </li>
          <li>
            Issuing required tax documentation (e.g., IRS Form 1099-NEC) to
            Workers earning $600 or more in a calendar year from that Employer
          </li>
        </BulletList>

        <SubHeading>5.2 Worker Acknowledgment</SubHeading>
        <BulletList>
          <li>
            Compensation for all engagements is the direct responsibility of
            the Employer, not Public Market Media LLC
          </li>
          <li>
            Public Market Media LLC has no obligation to pursue, guarantee, or
            recover payment on behalf of any Worker
          </li>
          <li>
            Workers are solely responsible for all taxes on earnings received
            from Employers
          </li>
          <li>
            Workers should consult a qualified tax professional regarding
            independent contractor tax obligations
          </li>
        </BulletList>

        <SubHeading>5.3 Payment Disputes</SubHeading>
        <p>
          Any compensation dispute is solely between the Employer and the
          Worker. Unresolved payment disputes may be reported through the
          Platform's feedback system, which may affect the Employer's standing
          on the Platform. Workers retain all rights to pursue claims directly
          against Employers through the California Labor Commissioner's
          Office, the Washington State Department of Labor &amp; Industries
          (L&amp;I), or applicable small claims court in their jurisdiction.
        </p>

        <SubHeading>5.4 Platform Pricing — Beta & Subscription Model</SubHeading>
        <Callout tone="accent">
          <strong>Beta Period — Free:</strong> Access to Public Market is
          completely FREE OF CHARGE to all Employers and Workers during the
          Beta testing period. No subscription fees, posting fees, or service
          charges of any kind will be assessed.
        </Callout>
        <p>
          Following the Beta period, access will transition to a
          subscription-based model paid solely by Employers/restaurants.
          Workers will always access the Platform at no charge. Subscription
          pricing and billing terms will be disclosed to Employers with advance
          notice before any charges are assessed. No fees will be charged
          without prior written or in-app disclosure and consent.
        </p>
      </DocSection>

      <DocSection
        id="tc-6"
        title="6. Liability Waivers — No-Shows & Workplace Incidents"
      >
        <Callout tone="warn">
          <strong>This section significantly affects your legal rights.</strong>
        </Callout>

        <SubHeading>6.1 No-Show Liability Waiver</SubHeading>
        <p>
          <strong>Workers:</strong> Failure to appear for a confirmed
          engagement ("No-Show") without providing at least 4 hours' advance
          notice to the Employer through the Platform may result in: (a)
          account suspension or termination; (b) negative platform reviews;
          and (c) forfeiture of any cancellation fees owed by the Employer.
        </p>
        <p>
          <strong>Employers:</strong> You release Public Market Media LLC from
          any and all claims, losses, damages, or liabilities arising from or
          related to Worker No-Shows, regardless of cause. Your sole recourse
          is through the Platform's review and dispute resolution system.
        </p>

        <SubHeading>6.2 Workplace Incident Liability Waiver</SubHeading>
        <p>
          Public Market Media LLC bears no responsibility for workplace
          incidents including but not limited to:
        </p>
        <BulletList>
          <li>
            Worker injuries, illnesses, or death occurring at an Employer's
            premises
          </li>
          <li>Property damage caused by Workers during an engagement</li>
          <li>
            Third-party claims arising from a Worker's negligent or intentional
            acts during an engagement
          </li>
          <li>
            Food safety violations, foodborne illness, or health code
            violations caused by Worker conduct
          </li>
          <li>
            Sexual harassment, discrimination, or workplace misconduct
            occurring during an engagement
          </li>
        </BulletList>
        <p>
          Employers bear sole responsibility for maintaining a safe workplace
          in compliance with Cal/OSHA standards (8 CCR § 3203 et seq.) in
          California, WISHA standards (WAC 296-800) in Washington, applicable
          county and local health regulations, and for obtaining appropriate
          commercial general liability and workers' compensation insurance in
          each state of operation.
        </p>

        <SubHeading>6.3 Indemnification</SubHeading>
        <p>
          Each user agrees to indemnify, defend, and hold harmless Public
          Market Media LLC, its members, managers, officers, employees,
          agents, and successors from and against any claims, damages, losses,
          liabilities, costs, and expenses (including reasonable attorneys'
          fees) arising out of or related to: (a) your use of the Platform;
          (b) your violation of these Terms; (c) your violation of any
          applicable law; or (d) any engagement facilitated through the
          Platform.
        </p>
      </DocSection>

      <DocSection id="tc-7" title="7. Data Privacy">
        <p>
          Public Market Media LLC collects, uses, and shares personal
          information as described in our Privacy Policy, which is incorporated
          herein by reference. By using the Platform, you consent to the
          collection and use of your information as described in the Privacy
          Policy. We comply with the California Consumer Privacy Act (CCPA),
          California Privacy Rights Act (CPRA), the Washington My Health MY
          Data Act (RCW 70.372), applicable Washington State privacy
          regulations, and applicable LA County and King County/Seattle privacy
          requirements.
        </p>
        <Callout tone="accent">
          <strong>We do not sell your personal information.</strong> We do not
          collect or store payment card numbers, bank account details, or any
          financial payment information.
        </Callout>
      </DocSection>

      <DocSection
        id="tc-8"
        title="8. Local Law Compliance — Los Angeles County & King County / Seattle"
      >
        <p>
          The Platform operates across multiple jurisdictions with distinct
          local labor, wage, and food safety laws. The following obligations
          apply based on the location of each engagement.
        </p>

        <SubHeading>8.1 Minimum Wage — California & Washington</SubHeading>
        <p>
          <strong>Los Angeles County:</strong> Compensation must meet or exceed
          applicable minimum wage requirements. Key jurisdictions include the
          City of Los Angeles (check lacity.org/ows), unincorporated LA County,
          Santa Monica, Long Beach, Pasadena, and other incorporated cities —
          each may have their own minimum wage ordinances.
        </p>
        <p>
          <strong>Seattle &amp; King County:</strong> Washington State minimum
          wage is set under RCW 49.46.020. The City of Seattle has its own
          higher minimum wage under the Seattle Minimum Wage Ordinance (SMC
          14.19). Employers should verify the current Seattle minimum wage
          tier applicable to their business size at seattle.gov/laborstandards.
          Unincorporated King County and other King County cities may have
          additional local requirements. Compliance is the sole responsibility
          of Employers and Workers.
        </p>

        <SubHeading>8.2 Gig Worker & Scheduling Protections</SubHeading>
        <p>
          <strong>Los Angeles County:</strong> Employers may be subject to fair
          work week or predictive scheduling ordinances in the City of Los
          Angeles and other LA County municipalities.
        </p>
        <p>
          <strong>Seattle:</strong> Seattle's Gig Worker Minimum Compensation
          Ordinance (SMC 14.34) may apply to certain app-based worker
          arrangements. Seattle's Secure Scheduling Ordinance (SMC 14.22)
          applies to retail and food service establishments with 500 or more
          employees worldwide. Employers are solely responsible for determining
          their obligations under applicable gig worker and scheduling laws.
        </p>

        <SubHeading>8.3 Food Handler Certification</SubHeading>
        <p>
          <strong>California:</strong> Workers must maintain a valid California
          Food Handler Card as required by the California Retail Food Code and
          LA County DPH regulations.
        </p>
        <p>
          <strong>Washington:</strong> Workers must maintain a valid Washington
          State Food Worker Card as required under WAC 246-215 and regulations
          administered by Seattle &amp; King County Public Health. Employers
          are responsible for verifying that all Workers hold current, valid
          certifications for the jurisdiction in which they are working before
          permitting them to handle food.
        </p>

        <SubHeading>8.4 Background Checks & Fair Chance</SubHeading>
        <p>
          <strong>California:</strong> Optional background checks may be
          facilitated with Worker consent under California's ICRAA. Employers
          must comply with the California Fair Chance Act and the City of Los
          Angeles Fair Chance Initiative for Hiring.
        </p>
        <p>
          <strong>Washington / Seattle:</strong> Employers must comply with
          Washington's Fair Chance Act (RCW 49.94) and the City of Seattle's
          Fair Chance Employment Ordinance (SMC 14.17), which restricts the
          use of criminal history in hiring decisions. Employers are solely
          responsible for compliance with applicable ban-the-box requirements
          in their jurisdiction.
        </p>

        <SubHeading>8.5 Workers' Compensation</SubHeading>
        <p>
          Workers' compensation requirements differ by state.{" "}
          <strong>California</strong> employers must maintain workers'
          compensation coverage under California Labor Code Section 3700 et
          seq. <strong>Washington State</strong> operates a state-run workers'
          compensation system administered by the Department of Labor &amp;
          Industries (L&amp;I) under RCW 51. Employers are solely responsible
          for maintaining proper workers' compensation coverage in each state
          in which they engage Workers.
        </p>

        <SubHeading>8.6 Seattle Paid Sick & Safe Time</SubHeading>
        <p>
          Seattle's Paid Sick and Safe Time Ordinance (SMC 14.16) may impose
          paid leave obligations on Employers engaging Workers in Seattle
          depending on the nature and duration of the engagement and the
          Employer's classification of Workers. Employers are solely
          responsible for evaluating and complying with applicable paid leave
          laws.
        </p>
      </DocSection>

      <DocSection
        id="tc-9"
        title="9. Limitation of Liability & Disclaimer of Warranties"
      >
        <p>
          THE PLATFORM IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
          WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. THIS IS A BETA
          PLATFORM AND MAY CONTAIN BUGS, ERRORS, OR INACCURACIES.
        </p>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PUBLIC MARKET
          MEDIA LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES. IN NO EVENT SHALL OUR
          TOTAL LIABILITY EXCEED THE GREATER OF: (A) FEES PAID TO US IN THE
          THREE MONTHS PRECEDING THE CLAIM; OR (B) $100.00.
        </p>
      </DocSection>

      <DocSection id="tc-10" title="10. Dispute Resolution & Arbitration">
        <SubHeading>10.1 Informal Resolution</SubHeading>
        <p>
          Before initiating formal proceedings, contact legal@publicmarket.com
          and attempt informal resolution for at least 30 calendar days.
        </p>
        <SubHeading>10.2 Binding Arbitration</SubHeading>
        <p>
          Unresolved disputes shall be resolved by binding individual
          arbitration administered by JAMS under its Streamlined Arbitration
          Rules, in Los Angeles, California. The arbitrator's decision is
          final and binding.
        </p>
        <SubHeading>10.3 Class Action Waiver</SubHeading>
        <p>
          You and Public Market Media LLC agree that each may bring claims
          only in an individual capacity and not as a plaintiff or class
          member in any class action, collective action, or representative
          proceeding.
        </p>
        <Callout tone="info">
          <strong>California Note:</strong> Certain claims under California's
          Private Attorneys General Act (PAGA) (Cal. Lab. Code § 2698 et seq.)
          may not be subject to binding arbitration as a matter of California
          law. Nothing herein is intended to waive non-waivable PAGA
          representative claims.
          <br />
          <br />
          <strong>Washington Note:</strong> Washington workers may have rights
          under the Washington Industrial Welfare Act (RCW 49.12) and other
          state labor statutes that may limit mandatory arbitration of certain
          wage claims. Nothing herein is intended to waive non-waivable
          statutory rights under Washington law.
        </Callout>
        <SubHeading>10.4 Governing Law</SubHeading>
        <p>
          These Terms are governed by the laws of the State of California. For
          disputes not subject to arbitration, you consent to exclusive
          jurisdiction in state and federal courts located in Los Angeles
          County, California.
        </p>
      </DocSection>

      <DocSection id="tc-11" title="11. Beta Platform Disclosures">
        <Callout tone="info">
          <strong>
            This is a Beta version of the Platform offered for a limited test
            period.
          </strong>{" "}
          The Platform may contain errors or incomplete features. Features,
          pricing, and terms may change materially before general release. We
          may discontinue the Beta at any time without notice. Data created
          during Beta may not be migrated to the general release version. Your
          participation is voluntary and at your own risk.
        </Callout>
      </DocSection>

      <DocSection id="tc-12" title="12. Intellectual Property">
        <p>
          All content on the Platform — including logos, trademarks, software,
          text, graphics, and design — is the property of Public Market Media
          LLC or its licensors. You are granted a limited, non-exclusive,
          non-transferable, revocable license to use the Platform solely for
          its intended purpose. You may not copy, modify, distribute, sell, or
          exploit any Platform content without our prior written consent.
        </p>
      </DocSection>

      <DocSection id="tc-13" title="13. Termination">
        <p>
          Public Market Media LLC may suspend or terminate your account at any
          time, with or without cause or notice. You may terminate your account
          by contacting support@publicmarket.com. Upon termination, your right
          to use the Platform immediately ceases and you remain liable for any
          outstanding payment obligations. Sections 2, 6, 7, 9, 10, and 14
          survive termination.
        </p>
      </DocSection>

      <DocSection id="tc-14" title="14. General Provisions">
        <BulletList>
          <li>
            <strong>Entire Agreement:</strong> These Terms, together with the
            Privacy Policy, constitute the entire agreement between you and
            Public Market Media LLC
          </li>
          <li>
            <strong>Severability:</strong> If any provision is found invalid,
            it shall be modified to the minimum extent necessary to make it
            enforceable; remaining provisions remain in full force
          </li>
          <li>
            <strong>Waiver:</strong> Our failure to enforce any provision is
            not a waiver of our right to enforce it in the future
          </li>
          <li>
            <strong>Assignment:</strong> You may not assign your rights without
            our prior written consent; we may assign freely to affiliates or
            successors
          </li>
          <li>
            <strong>Force Majeure:</strong> We are not liable for delays or
            failures due to causes beyond our reasonable control
          </li>
          <li>
            <strong>Notices:</strong> Legal notices to us should be sent to:
            legal@publicmarket.com
          </li>
        </BulletList>
        <Callout tone="info">
          <strong>Contact:</strong> legal@publicmarket.com ·
          support@publicmarket.com · privacy@publicmarket.com
        </Callout>
      </DocSection>

      <DocSection id="tc-15" title="15. Messaging & SMS Program">
        <p>
          By providing your mobile number and opting in, you consent to receive
          text messages from Public Market at that number, including via
          automated dialing technology, for account, verification, and — where
          you have separately opted in — informational and reminder messages.
          Consent to receive marketing or informational texts is not a condition
          of using the Platform.
        </p>
        <BulletList>
          <li>Message frequency varies.</li>
          <li>Message and data rates may apply.</li>
          <li>
            Reply <strong>STOP</strong> to any message to unsubscribe; reply{" "}
            <strong>HELP</strong> for help, or contact support@publicmarket.com.
          </li>
          <li>Carriers are not liable for delayed or undelivered messages.</li>
        </BulletList>
        <p>
          See our Privacy Policy for how we handle information associated with
          text messaging. Mobile opt-in information and consent are never shared
          with third parties for marketing purposes.
        </p>
      </DocSection>

      <footer className="mt-6 pt-3 border-t border-garnet/15 text-[11.5px] text-slate">
        © 2026 Public Market Media LLC. All rights reserved.
      </footer>
    </article>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* PRIVACY POLICY                                                              */
/* Text taken verbatim from newTandC.html                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

function SummaryTable({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <table className="w-full border-collapse text-[13px] my-3">
      <tbody>
        {rows.map(([label, value], i) => (
          <tr key={i}>
            <td className="align-top p-2.5 border border-garnet/15 bg-garnet/5 font-semibold text-garnet w-[170px]">
              {label}
            </td>
            <td className="align-top p-2.5 border border-garnet/15 text-cocoa">
              {value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PrivacyContent() {
  return (
    <article>
      <MetaHeader
        eyebrow="Public Market Media LLC"
        title="Privacy Policy"
        version={PP_VERSION}
      />

      <DocSection id="pp-glance" title="Privacy at a Glance">
        <SummaryTable
          rows={[
            [
              "Who we are",
              "Public Market Media LLC — a food service workforce marketplace connecting Workers and Employers in LA County, King County / Seattle, and beyond.",
            ],
            [
              "Data we collect",
              "Name, contact info, work history, certifications, device/usage data, and approximate location (with consent).",
            ],
            [
              "Why we collect it",
              "To operate the Platform, match Workers with Employers, comply with law, and improve our services.",
            ],
            [
              "Do we sell data?",
              "No. We do not sell your personal information to third parties.",
            ],
            [
              "Do we share data?",
              "Only with service providers who help us operate the Platform, or as required by law.",
            ],
            [
              "Your CA rights",
              "Right to know, delete, correct, opt-out of sharing, and non-discrimination under CCPA/CPRA.",
            ],
            [
              "Your WA rights",
              "Rights under the Washington My Health MY Data Act (RCW 70.372) and applicable Washington state privacy law.",
            ],
            [
              "Payment data",
              "We do not process payments. All compensation flows directly between Employers and Workers.",
            ],
            [
              "Text messaging",
              "Mobile opt-in data and SMS consent are never shared with third parties for marketing. Reply STOP to opt out.",
            ],
            ["Contact", "privacy@publicmarket.com"],
          ]}
        />
      </DocSection>

      <Callout tone="warn">
        This Privacy Policy explains how Public Market Media LLC collects,
        uses, shares, and protects your personal information when you use the
        Platform. By using the Platform, you consent to the practices
        described herein.
      </Callout>

      <DocSection id="pp-1" title="1. Who This Policy Applies To">
        <p>
          This Privacy Policy applies to food service Workers who register or
          use the Platform to find engagements, Employers and restaurant
          representatives who post shifts and connect with Workers, and
          visitors who browse the Platform without registering.
        </p>
      </DocSection>

      <DocSection id="pp-2" title="2. Information We Collect">
        <SubHeading>2.1 Information You Provide — All Users</SubHeading>
        <BulletList>
          <li>Full name, email address, phone number, and date of birth</li>
          <li>Username and password</li>
          <li>Profile photo (optional)</li>
          <li>Communications sent through the Platform</li>
        </BulletList>

        <SubHeading>2.2 Workers</SubHeading>
        <BulletList>
          <li>Work history, skills, and experience in the food service industry</li>
          <li>
            Food handler certifications, ServSafe credentials, and other
            required licenses
          </li>
          <li>
            Government-issued ID or work authorization documentation (for
            identity verification only)
          </li>
          <li>Availability and preferred work locations</li>
          <li>Ratings and reviews received from Employers</li>
        </BulletList>

        <SubHeading>2.3 Employers</SubHeading>
        <BulletList>
          <li>Business name, type, and address</li>
          <li>Contact person name and role</li>
          <li>Business license or permit information</li>
          <li>
            Job posting details including shift requirements, compensation
            rates, and location
          </li>
          <li>Ratings and reviews submitted about Workers</li>
        </BulletList>

        <SubHeading>2.4 Automatically Collected Information</SubHeading>
        <BulletList>
          <li>
            Device identifiers, IP address, browser type, and operating system
          </li>
          <li>Pages viewed, features used, and time spent on the Platform</li>
          <li>Cookie and similar tracking technology data</li>
        </BulletList>

        <SubHeading>2.5 Location Information</SubHeading>
        <p>
          With your consent, we may collect approximate geolocation data to
          match Workers with nearby opportunities. You may disable location
          sharing in your device settings; this may limit functionality. We do
          not track your precise real-time location continuously.
        </p>

        <Callout tone="accent">
          <strong>What we do NOT collect:</strong> Public Market Media LLC does
          not collect, store, or process payment card numbers, bank account
          details, or any financial payment information. All compensation is
          paid directly between Employers and Workers outside of the Platform.
        </Callout>
      </DocSection>

      <DocSection id="pp-3" title="3. How We Use Your Information">
        <SubHeading>3.1 Platform Operations</SubHeading>
        <BulletList>
          <li>Creating and maintaining your account</li>
          <li>Matching Workers with relevant Employer opportunities</li>
          <li>
            Displaying Worker profiles to Employers and Employer listings to
            Workers
          </li>
          <li>Processing engagement confirmations and cancellations</li>
          <li>
            Sending Platform notifications, shift reminders, and account
            updates
          </li>
        </BulletList>

        <SubHeading>3.2 Safety & Verification</SubHeading>
        <BulletList>
          <li>Verifying user identity and eligibility to use the Platform</li>
          <li>Verifying food handler certifications and required licenses</li>
          <li>Facilitating optional background checks (with Worker consent only)</li>
          <li>
            Investigating reports of misconduct, fraud, or violations of our
            Terms
          </li>
        </BulletList>

        <SubHeading>3.3 Communications & Marketing</SubHeading>
        <BulletList>
          <li>Responding to your support requests and inquiries</li>
          <li>
            Sending service-related emails (account confirmations, policy
            updates)
          </li>
          <li>
            Sending marketing communications about Platform features (only with
            consent; opt out at any time)
          </li>
        </BulletList>

        <SubHeading>3.4 Legal Compliance</SubHeading>
        <BulletList>
          <li>Complying with applicable California and federal laws and regulations</li>
          <li>
            Responding to valid legal process, court orders, or government
            requests
          </li>
          <li>
            Enforcing our Terms &amp; Conditions and protecting the rights of
            Platform users
          </li>
        </BulletList>
      </DocSection>

      <DocSection id="pp-4" title="4. How We Share Your Information">
        <Callout tone="accent">
          <strong>We do not sell your personal information. Period.</strong>
        </Callout>

        <SubHeading>4.1 Between Platform Users</SubHeading>
        <BulletList>
          <li>
            Worker profiles (name, photo, experience, certifications, ratings)
            are visible to Employers
          </li>
          <li>
            Employer listings (business name, location, shift details, ratings)
            are visible to Workers
          </li>
          <li>
            We do not share Worker government ID or sensitive verification
            documents with Employers
          </li>
        </BulletList>

        <SubHeading>4.2 Service Providers</SubHeading>
        <p>
          We share information with vendors who help us operate the Platform
          (cloud hosting, analytics, customer support, notification delivery,
          background check providers). All are contractually required to use
          your information only to provide services to us, maintain appropriate
          security, and comply with applicable privacy laws.
        </p>

        <SubHeading>4.3 Legal Requirements</SubHeading>
        <p>
          We may disclose your information when required by law, in response
          to valid subpoenas or court orders, to protect the rights or safety
          of users, or to detect and prevent fraud or illegal activity.
        </p>

        <SubHeading>4.4 Business Transfers</SubHeading>
        <p>
          If Public Market Media LLC is acquired or merges with another
          company, your information may transfer as part of that transaction.
          We will notify you before your information becomes subject to a
          different privacy policy.
        </p>
      </DocSection>

      <DocSection id="pp-5" title="5. Your California Privacy Rights (CCPA / CPRA)">
        <p>
          If you are a California resident, you have the following rights:
        </p>
        <BulletList>
          <li>
            <strong>Right to Know:</strong> Request disclosure of the
            categories and specific pieces of personal information we have
            collected about you
          </li>
          <li>
            <strong>Right to Delete:</strong> Request deletion of your personal
            information (subject to certain exceptions)
          </li>
          <li>
            <strong>Right to Correct:</strong> Request correction of inaccurate
            personal information we hold about you
          </li>
          <li>
            <strong>Right to Opt-Out:</strong> We do not sell your personal
            information. Contact us if you believe any sharing should be
            limited
          </li>
          <li>
            <strong>Right to Limit Sensitive Info:</strong> Limit our use of
            sensitive personal information such as government ID or precise
            geolocation
          </li>
          <li>
            <strong>Right to Non-Discrimination:</strong> We will not
            discriminate against you for exercising any privacy right
          </li>
        </BulletList>
        <Callout tone="info">
          <strong>To exercise your rights:</strong>
          <br />
          Email: privacy@publicmarket.com &nbsp;·&nbsp; Phone: [INSERT TOLL-FREE
          NUMBER]
          <br />
          We will respond within 45 calendar days as required by California
          law.
        </Callout>

        <SubHeading>5.7 Washington State Privacy Rights</SubHeading>
        <p>
          If you are a Washington State resident, you have additional rights
          under the Washington My Health MY Data Act (RCW 70.372) and
          applicable Washington state privacy law, including:
        </p>
        <BulletList>
          <li>
            <strong>Right to Access:</strong> Request a copy of the personal
            data we hold about you
          </li>
          <li>
            <strong>Right to Correction:</strong> Request correction of
            inaccurate personal data
          </li>
          <li>
            <strong>Right to Deletion:</strong> Request deletion of your
            personal data, subject to certain exceptions
          </li>
          <li>
            <strong>Right to Data Portability:</strong> Receive your personal
            data in a portable format
          </li>
          <li>
            <strong>Right to Opt-Out:</strong> Opt out of the sale of personal
            data, targeted advertising, and profiling in furtherance of
            decisions that produce legal or similarly significant effects (we
            do not engage in any of these activities)
          </li>
          <li>
            <strong>Right to Appeal:</strong> If we deny your privacy request,
            you have the right to appeal our decision
          </li>
        </BulletList>
        <Callout tone="info">
          <strong>Washington residents:</strong> To exercise your rights or
          appeal a decision, contact privacy@publicmarket.com with subject
          line: <em>"Washington Privacy Request."</em> We will respond within
          45 days, with a possible 45-day extension. If you are unsatisfied
          with our response, you may contact the Washington State Attorney
          General at atg.wa.gov.
        </Callout>
      </DocSection>

      <DocSection id="pp-6" title="6. Data Retention">
        <p>
          We retain your personal information as long as your account is
          active, or as necessary to provide services, comply with legal
          obligations, resolve disputes, and fulfill other legitimate business
          purposes.
        </p>
        <SummaryTable
          rows={[
            ["Account data", "Retained while active + 2 years after deletion"],
            ["Engagement records", "3 years from engagement date"],
            ["Certification records", "Duration of account + 1 year"],
            ["Communications / messages", "1 year from date of communication"],
            ["Analytics / usage data", "2 years, then anonymized"],
            [
              "Legal compliance records",
              "As required by law (typically 3–7 years)",
            ],
          ]}
        />
        <p>
          When you delete your account, we will delete or anonymize your
          personal information within 45 days, except where required to retain
          by law.
        </p>
      </DocSection>

      <DocSection id="pp-7" title="7. Data Security">
        <p>
          We implement commercially reasonable administrative, technical, and
          physical security measures including encryption of data in transit
          (TLS/SSL), need-to-know access controls, regular security
          assessments, and incident response procedures.
        </p>
        <Callout tone="warn">
          No method of electronic transmission or storage is 100% secure. In
          the event of a data breach posing significant risk of harm, we will
          notify you as required by California Civil Code § 1798.82.
        </Callout>
      </DocSection>

      <DocSection id="pp-8" title="8. Cookies & Tracking Technologies">
        <SummaryTable
          rows={[
            [
              "Strictly Necessary",
              "Required for the Platform to function (e.g., session authentication). Cannot be disabled.",
            ],
            [
              "Analytics",
              "Help us understand how users interact with the Platform. Can be opted out of.",
            ],
            [
              "Preference",
              "Remember your settings and preferences. Can be opted out of.",
            ],
            [
              "Marketing / Advertising",
              "We do not currently use third-party advertising cookies.",
            ],
          ]}
        />
        <p>
          You may control cookies through your browser settings. We do not use
          cookies to sell your personal information or for cross-context
          behavioral advertising.
        </p>
      </DocSection>

      <DocSection id="pp-9" title="9. Children's Privacy">
        <p>
          The Platform is not directed to individuals under 18 years of age
          and we do not knowingly collect personal information from minors. If
          we become aware we have collected information from a person under
          18, we will delete it promptly. Contact privacy@publicmarket.com if
          you believe we have inadvertently collected a minor's information.
        </p>
      </DocSection>

      <DocSection id="pp-10" title="10. Third-Party Links & Integrations">
        <p>
          The Platform may contain links to third-party websites or
          integrations with third-party services. This Privacy Policy does not
          apply to those third-party services. We encourage you to review the
          privacy policies of any third-party sites you visit. Public Market
          Media LLC is not responsible for the privacy practices of third
          parties.
        </p>
      </DocSection>

      <DocSection id="pp-11" title="11. Jurisdiction-Specific Privacy Disclosures">
        <SubHeading>
          11.1 Los Angeles County & Incorporated Cities — California
        </SubHeading>
        <p>
          In addition to CCPA/CPRA compliance, we comply with applicable data
          protection requirements under the Los Angeles Municipal Code and
          ordinances in Santa Monica, Pasadena, Long Beach, and other
          incorporated LA County cities to the extent they apply to our
          operations. If you are based in a specific LA County city with
          additional local privacy rights, those rights supplement (not
          replace) your CCPA rights described in Section 5.
        </p>

        <SubHeading>
          11.2 King County, City of Seattle & Washington State
        </SubHeading>
        <p>
          For users located in or operating within Washington State, King
          County, or the City of Seattle, we comply with applicable Washington
          State privacy requirements including the Washington My Health MY
          Data Act (RCW 70.372) and Washington's general privacy principles
          under the Consumer Protection Act (RCW 19.86). We also comply with
          applicable Seattle and King County data governance requirements to
          the extent they apply to our Platform operations.
        </p>
        <p>
          Seattle and King County users have all rights described in Section
          5.7 of this Privacy Policy. The City of Seattle's Office of Civil
          Rights (seattle.gov/civilrights) may be a resource for
          privacy-related concerns arising from Platform use in Seattle.
        </p>

        <SubHeading>11.3 Multi-State Users</SubHeading>
        <p>
          If you use the Platform in both California and Washington (for
          example, a Worker who takes engagements in both states), both sets
          of rights apply to you with respect to data processed in connection
          with each state's activities. You may exercise rights under either
          or both frameworks by contacting privacy@publicmarket.com and
          specifying the applicable state.
        </p>
      </DocSection>

      <DocSection id="pp-12" title="12. Beta Period Privacy Considerations">
        <Callout tone="info">
          During the Beta testing period, the Platform may collect additional
          usage data, error logs, and feedback to improve the product. Beta
          users may be asked to participate in surveys or interviews.
          Participation is voluntary. Data collected during Beta is subject to
          this Privacy Policy and retained or deleted consistent with Section
          6.
        </Callout>
        <p>
          As a Beta user, you acknowledge that additional diagnostic data may
          be collected to identify and fix bugs, feedback you provide may be
          used to develop and improve the Platform, and Platform privacy
          features and controls may evolve before general release.
        </p>
      </DocSection>

      <DocSection id="pp-13" title="13. Changes to This Privacy Policy">
        <p>
          We may update this Privacy Policy from time to time. When we make
          material changes, we will post the updated policy with a new
          effective date, send email notification to the address on file, and
          provide at least 14 days' notice before material changes take
          effect. Continued use of the Platform after the effective date
          constitutes acceptance of the revised Policy.
        </p>
      </DocSection>

      <DocSection id="pp-14" title="14. Contact Us">
        <Callout tone="info">
          <strong>Public Market Media LLC</strong>
          <br />
          <br />
          Email: privacy@publicmarket.com
          <br />
          General Support: support@publicmarket.com
          <br />
          Phone: [INSERT TOLL-FREE NUMBER]
          <br />
          <br />
          For California CCPA requests, use subject line:{" "}
          <em>"California Privacy Request"</em>
          <br />
          For Washington privacy requests, use subject line:{" "}
          <em>"Washington Privacy Request"</em>
          <br />
          <br />
          We respond within 45 calendar days. Unsatisfied California residents
          may contact the California Privacy Protection Agency at cppa.ca.gov.
          Unsatisfied Washington residents may contact the Washington State
          Attorney General at atg.wa.gov.
        </Callout>
      </DocSection>

      <DocSection id="pp-15" title="15. SMS / Text Messaging & Mobile Data">
        <p>
          When you provide your mobile phone number and opt in, we use it to
          send account, verification, transactional, and — with your separate
          consent — informational or reminder text messages. Message frequency
          varies and message and data rates may apply. You can reply STOP to opt
          out at any time, or HELP for help.
        </p>
        <Callout tone="accent">
          <strong>
            No mobile information (including phone numbers and SMS
            opt-in/consent) will be shared with or sold to third parties or
            affiliates for marketing or promotional purposes.
          </strong>{" "}
          Sharing with the subcontractors who help us deliver the messaging
          service (for example, our SMS provider) is limited to delivering the
          messages you requested.
        </Callout>
      </DocSection>

      <footer className="mt-6 pt-3 border-t border-garnet/15 text-[11.5px] text-slate flex flex-wrap justify-between gap-1">
        <span>© 2026 Public Market Media LLC. All rights reserved.</span>
        <span>Beta v{PP_VERSION} · {EFFECTIVE_DATE} · Los Angeles, CA</span>
      </footer>
    </article>
  );
}
